import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, Radio, message } from 'antd';
import { COPY } from '@/lib/copyTokens';
import { canAccessInternalAdsTools } from '@/lib/adsToolsAccess';
import { apiClient } from '@/services/apiClient';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import type { TenantSettings, TenantStatus } from '@/types/tenant';

const STATUSES: readonly TenantStatus[] = ['active', 'paused', 'offboarded'];

interface StatusChangeResponse {
  website: string;
  status: TenantStatus;
  previous_status: TenantStatus | null;
  changed: boolean;
}

export interface ClientStatusControlProps {
  website: string;
  /**
   * When known by the caller (e.g. the /clients roster already carries it per
   * row), pass it directly. When absent, this control reads
   * /tenant-settings/{website} itself.
   */
  currentStatus?: TenantStatus | null;
  onStatusChanged?: (next: TenantStatus) => void;
  /**
   * `'modal'` (default) keeps the settings-rail card behaviour: a Radio group
   * that opens an antd Modal to confirm. `'inline'` swaps to a shadcn Select
   * with Save / Cancel buttons in the same row, no dialog on top, used by
   * the /clients Status column so the cell edits in place.
   */
  confirm?: 'modal' | 'inline';
  onCancel?: () => void;
}

/**
 * Headless client-status control (staff only): the confirm + PATCH the
 * settings card renders today, lifted so the /clients roster can render one
 * per tenant row without duplicating the write path.
 */
export function ClientStatusControl({
  website,
  currentStatus,
  onStatusChanged,
  confirm: confirmMode = 'modal',
  onCancel,
}: ClientStatusControlProps) {
  const userProfile = useWorkspaceStore((state) => state.userProfile);
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<TenantStatus | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const copy = COPY.sections.businessInfo.clientStatus;

  // The column is NOT NULL DEFAULT 'active'; an older API without the field
  // means active, not unknown.
  const hasProvidedStatus = currentStatus !== undefined;
  const settingsQuery = useQuery({
    queryKey: ['tenantSettings', website],
    queryFn: async () => {
      const response = await apiClient.get<TenantSettings>(
        `/tenant-settings/${encodeURIComponent(website)}`,
      );
      return response.data;
    },
    enabled: Boolean(website) && !hasProvidedStatus,
    staleTime: 60_000,
  });

  if (!canAccessInternalAdsTools(userProfile) || !website) return null;

  // Two shapes of "not known":
  //  - the caller passed no prop at all (undefined): the DB default is
  //    'active' (NOT NULL DEFAULT 'active'), so an older API that omits the
  //    field means Active and we preselect it.
  //  - the caller passed explicit null (e.g. the pill got "unknown" from a
  //    value outside the CHECK, ' Paused ' with whitespace, or a lagging
  //    schema): we must NOT preselect Active (Viktor #2627), or a click on
  //    Active is a no-op and the row stays unfixable from this UI.
  const explicitUnknown = hasProvidedStatus && currentStatus === null;
  const loadedStatus: TenantStatus | null = hasProvidedStatus
    ? currentStatus ?? null
    : (settingsQuery.data?.status as TenantStatus | undefined) ?? 'active';
  const current: TenantStatus | null = explicitUnknown ? null : loadedStatus;
  const isLoading = !hasProvidedStatus && settingsQuery.isLoading;

  const runPatch = async (next: TenantStatus) => {
    setIsSaving(true);
    try {
      const response = await apiClient.patch<StatusChangeResponse>(
        `/tenant-settings/${encodeURIComponent(website)}/status`,
        { status: next },
      );
      const saved = response.data.status;
      queryClient.setQueryData<TenantSettings | null>(
        ['tenantSettings', website],
        (old) => (old ? { ...old, status: saved } : old),
      );
      void queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === 'string' &&
          query.queryKey[0].startsWith('google-ads'),
      });
      // /clients does not use React Query for its roster (Viktor, #2627 and
      // #2632): it re-reads through the onStatusChanged callback below.
      message.success(copy.saved(copy.options[saved]));
      setPending(null);
      onStatusChanged?.(saved);
    } catch (error) {
      console.error('Failed to change client status', error);
      message.error(copy.failed);
    } finally {
      setIsSaving(false);
    }
  };

  if (confirmMode === 'inline') {
    // Inline mode: shadcn Select + Save/Cancel, no antd Modal on top. Used by
    // the /clients Status column so the cell edits in place.
    const canSave =
      pending !== null && !isSaving && (current === null || pending !== current);
    return (
      <div className="flex items-center gap-2">
        <Select
          value={pending ?? current ?? undefined}
          disabled={isLoading || isSaving}
          onValueChange={(next) => {
            if (!STATUSES.includes(next as TenantStatus)) return;
            setPending(next as TenantStatus);
          }}
        >
          <SelectTrigger
            className="h-8 w-36"
            aria-label={`Change status for ${website}`}
          >
            <SelectValue placeholder="Pick status" />
          </SelectTrigger>
          <SelectContent>
            {STATUSES.map((value) => (
              <SelectItem key={value} value={value}>
                {copy.options[value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          size="sm"
          className="h-8 bg-coral hover:bg-coral-hover"
          disabled={!canSave}
          onClick={() => {
            if (canSave && pending) void runPatch(pending);
          }}
        >
          Save
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8"
          disabled={isSaving}
          onClick={() => {
            setPending(null);
            onCancel?.();
          }}
        >
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Radio.Group
        value={current ?? undefined}
        disabled={isLoading || isSaving}
        onChange={(event) => {
          const next = event.target.value as TenantStatus;
          // Fire on any of the three when current is unknown, or on a
          // different value when current is known. Never re-write the same.
          if (!STATUSES.includes(next)) return;
          if (current === null || next !== current) setPending(next);
        }}
      >
        {STATUSES.map((value) => (
          <Radio.Button key={value} value={value}>
            {copy.options[value]}
          </Radio.Button>
        ))}
      </Radio.Group>
      <Modal
        open={pending !== null}
        title={pending ? copy.confirmTitle(website, copy.options[pending].toLowerCase()) : ''}
        okText={copy.confirmOk}
        cancelText={copy.cancel}
        okButtonProps={{ className: 'bg-coral hover:bg-coral-hover', loading: isSaving }}
        onOk={() => {
          if (pending) void runPatch(pending);
        }}
        onCancel={() => setPending(null)}
        confirmLoading={isSaving}
      >
        <p className="text-sm text-ink-2">{pending ? copy.confirmBody[pending] : null}</p>
      </Modal>
    </div>
  );
}
